import React, { Fragment, useRef, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XIcon } from '@heroicons/react/solid';
import StaticImage from './Image';

interface ImageWithModalProps {
  src: string;
  vertical?: boolean;
}

export default function ImageWithModal({
  src,
  vertical = false,
}: ImageWithModalProps) {
  const [open, setOpen] = useState(false);

  const cancelButtonRef = useRef(null);

  return (
    <>
      <div className="group relative text-base sm:text-sm">
        <button
          type="button"
          className="bg-transparent focus:outline-none outline-none w-full h-auto"
          onClick={(e) => {
            e.preventDefault();
            setOpen(true);
          }}
        >
          <StaticImage
            filename={`${src}`}
            alt="..."
            className={`rounded-lg bg-gray-100 overflow-hidden group-hover:opacity-75 ${
              vertical ? 'aspect-w-3 aspect-h-4' : 'aspect-w-1 aspect-h-1'
            }`}
            imgClassName="w-full h-full object-top object-cover"
          />
        </button>
      </div>
      <Transition.Root show={open} as={Fragment}>
        <Dialog
          as="div"
          className="fixed z-10 inset-0 overflow-y-auto"
          initialFocus={cancelButtonRef}
          onClose={setOpen}
        >
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
            </Transition.Child>

            {/* This element is to trick the browser into centering the modal contents. */}
            <span
              className="hidden sm:inline-block sm:align-middle sm:h-screen"
              aria-hidden="true"
            >
              &#8203;
            </span>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full p-8">
                <button
                  type="button"
                  className="absolute top-4 right-4 z-20"
                  onClick={(e) => {
                    e.preventDefault();
                    setOpen(false);
                  }}
                >
                  <XIcon className="w-6 h-6 text-gray-400" />
                </button>
                <StaticImage
                  filename={`${src}`}
                  alt="..."
                  className="aspect-w-1 aspect-h-1 rounded-lg overflow-hidden group-hover:opacity-75"
                  imgClassName="w-full h-full object-center object-contain"
                  imgStyle={{
                    objectFit: 'contain',
                  }}
                />
              </div>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
}
